import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Search, UserPlus, CheckCircle, XCircle, Edit, Building, User, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search query
    if (searchQuery) {
      const filtered = users.filter(user =>
        user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      // API now returns paginated response: {users: [], total, page, ...}
      const usersArray = response.data.users || response.data || [];
      setUsers(usersArray);
      setFilteredUsers(usersArray);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les utilisateurs",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, {
        isActive: !currentStatus
      });
      
      toast({
        title: "Statut modifié",
        description: `Utilisateur ${currentStatus ? 'désactivé' : 'activé'} avec succès`
      });
      
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le statut",
        variant: "destructive"
      });
    }
  };

  const openEditModal = (user) => {
    setEditUser({ ...user });
    setIsEditModalOpen(true);
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/users/${editUser.id}`, {
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        phone: editUser.phone,
        accountType: editUser.accountType || 'particulier',
        companyName: editUser.accountType === 'entreprise' ? editUser.companyName : ''
      });
      toast({ title: "Utilisateur modifié", description: "Les modifications ont été enregistrées" });
      setIsEditModalOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast({ title: "Erreur", description: error.response?.data?.detail || "Impossible de modifier l'utilisateur", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
          <p className="text-gray-500 mt-1">Gérez les comptes clients de la plateforme</p>
        </div>
        <Button className="bg-gradient-to-r from-[#1db584] to-[#16a075] hover:from-[#16a075] hover:to-[#128f69] text-white">
          <UserPlus className="w-5 h-5 mr-2" />
          Nouvel utilisateur
        </Button>
      </div>

      {/* Search and Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="md:col-span-2">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Utilisateurs</p>
              <p className="text-xl md:text-3xl font-bold text-gray-900">{users.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">Actifs</p>
              <p className="text-xl md:text-3xl font-bold text-green-600">
                {users.filter(u => u.isActive).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Utilisateurs</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Inscrit le</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Aucun utilisateur trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>
                      <Badge variant={user.accountType === 'entreprise' ? 'default' : 'secondary'}>
                        {user.accountType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.isActive ? (
                        <Badge variant="success">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleUserStatus(user.id, user.isActive)}
                        >
                          {user.isActive ? 'Désactiver' : 'Activer'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditModal(user)} data-testid={`edit-user-${user.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#1db584]" />
              Modifier l&apos;utilisateur
            </DialogTitle>
            <DialogDescription>
              {editUser && `${editUser.firstName} ${editUser.lastName} (${editUser.email})`}
            </DialogDescription>
          </DialogHeader>
          
          {editUser && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input value={editUser.firstName || ''} onChange={(e) => setEditUser({...editUser, firstName: e.target.value})} data-testid="edit-user-firstname" />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input value={editUser.lastName || ''} onChange={(e) => setEditUser({...editUser, lastName: e.target.value})} data-testid="edit-user-lastname" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={editUser.phone || ''} onChange={(e) => setEditUser({...editUser, phone: e.target.value})} data-testid="edit-user-phone" />
              </div>
              
              {/* Type de compte */}
              <div className="space-y-2">
                <Label>Type de compte</Label>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant={editUser.accountType !== 'entreprise' ? 'default' : 'outline'} 
                    className={editUser.accountType !== 'entreprise' ? 'bg-[#1db584] hover:bg-[#19a074] flex-1' : 'flex-1'} 
                    onClick={() => setEditUser({...editUser, accountType: 'particulier', companyName: ''})}
                    data-testid="edit-user-type-particulier"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Particulier
                  </Button>
                  <Button 
                    type="button" 
                    variant={editUser.accountType === 'entreprise' ? 'default' : 'outline'} 
                    className={editUser.accountType === 'entreprise' ? 'bg-[#1db584] hover:bg-[#19a074] flex-1' : 'flex-1'} 
                    onClick={() => setEditUser({...editUser, accountType: 'entreprise'})}
                    data-testid="edit-user-type-entreprise"
                  >
                    <Building className="w-4 h-4 mr-2" />
                    Entreprise
                  </Button>
                </div>
              </div>
              
              {/* Nom d'entreprise - visible uniquement pour type entreprise */}
              {editUser.accountType === 'entreprise' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Nom de l&apos;entreprise
                  </Label>
                  <Input 
                    value={editUser.companyName || ''} 
                    onChange={(e) => setEditUser({...editUser, companyName: e.target.value})} 
                    placeholder="Nom de l'entreprise..."
                    data-testid="edit-user-companyname"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Annuler</Button>
            <Button onClick={handleEditUser} disabled={saving} className="bg-[#1db584] hover:bg-[#19a074]" data-testid="edit-user-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
